import React from 'react';
import { formatDateInTimezone, getTodayInTimezone, toDateKeyInTimezone } from '../utils/dateUtils';

// Shared display helpers for the Customers list and its slide-over.

export const customerName = (c) => `${c.firstName || ''} ${c.lastName || ''}`.trim();

// Phone-only and imported customers get a generated address so the email
// column can stay unique; never show those.
export const isPlaceholderEmail = (email) => /@(phone\.)?noemail\.local$/i.test(email || '');

export const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
};

export const formatCurrency = (amount, { whole = false } = {}) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount || 0);

const dayDiff = (a, b) => Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000);

// "Jul 21 · today", "Jul 20 · yesterday", "Jul 18", "Dec 3, 2025".
export const relativeDay = (date) => {
  const key = toDateKeyInTimezone(date);
  if (!key) return '';
  const today = getTodayInTimezone();
  const sameYear = key.slice(0, 4) === today.slice(0, 4);
  const label = formatDateInTimezone(key, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  const diff = dayDiff(key, today);
  if (diff === 0) return `${label} · today`;
  if (diff === -1) return `${label} · yesterday`;
  if (diff === 1) return `${label} · tomorrow`;
  return label;
};

const initials = (c) => `${(c.firstName || '').charAt(0)}${(c.lastName || '').charAt(0)}`.toUpperCase() || '?';

// House accounts get the sage tone, like their tag; everyone else is neutral.
export const CustomerAvatar = ({ customer, size = 'md' }) => {
  const sizes = { md: 'h-[34px] w-[34px] text-xs', lg: 'h-11 w-11 text-[15px]' };
  const tone = customer.isHouseAccount ? 'bg-accent2-200 text-accent2-800' : 'bg-gray-200 text-gray-800';
  return (
    <span
      aria-hidden="true"
      className={`grid flex-shrink-0 place-items-center rounded-full font-bold ${sizes[size]} ${tone}`}
    >
      {initials(customer)}
    </span>
  );
};
